/**
 * Notification Bell — header dropdown (Alerts Phase 1).
 *
 * Polls `/api/alerts/unread-count` every 30s for the badge counter and lazily
 * loads the full list when the popover opens. Clicking a row marks it read
 * and (if it has a `link`) navigates to the deep link. "Mark all as read"
 * clears the badge in one server round-trip.
 *
 * All fetches go through the generated React Query hooks, which in turn use
 * `customFetch` (Clerk Bearer required — `fetch()` cookies don't authenticate
 * `/api/*` in this app).
 */
import { useState } from "react";
import { Link } from "wouter";
import { Bell, Check, CheckCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetAlertsUnreadCount,
  useListAlerts,
  useMarkAlertRead,
  useMarkAllAlertsRead,
  getGetAlertsUnreadCountQueryKey,
  getListAlertsQueryKey,
} from "@workspace/api-client-react";
import type { Alert, AlertPriority } from "@workspace/api-client-react";
import { useJitteredInterval } from "@/lib/jitter";
import { useStatusSummary, STATUS_SUMMARY_QUERY_KEY } from "@/hooks/use-status-summary";

function timeAgo(iso: string): string {
  const diffSec = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (diffSec < 60) return `${diffSec}s ago`;
  const m = Math.floor(diffSec / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

// Highest-priority-wins ranking used to colour the badge and per-row dots.
const PRIORITY_RANK: Record<AlertPriority, number> = {
  urgent: 3,
  high: 2,
  normal: 1,
  low: 0,
};

/** Background colour for the unread badge, by highest unread priority. */
function badgeColorClass(priority: AlertPriority | null): string {
  switch (priority) {
    case "urgent":
      return "bg-red-600";
    case "high":
      return "bg-orange-500";
    case "normal":
      return "bg-blue-500";
    case "low":
      return "bg-slate-400";
    default:
      return "bg-red-600";
  }
}

/** Per-row unread dot colour, by that row's priority. */
function dotColorClass(priority: AlertPriority): string {
  switch (priority) {
    case "urgent":
      return "bg-red-600";
    case "high":
      return "bg-orange-500";
    case "normal":
      return "bg-blue-500";
    case "low":
    default:
      return "bg-slate-400";
  }
}

/** Highest priority among the unread items, or null when none. */
function highestUnreadPriority(items: Alert[]): AlertPriority | null {
  let best: AlertPriority | null = null;
  for (const a of items) {
    if (a.read) continue;
    if (best === null || PRIORITY_RANK[a.priority] > PRIORITY_RANK[best]) {
      best = a.priority;
    }
  }
  return best;
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();

  // Unified badge polling (Task #1958): read the alert count from the shared
  // status-summary cache that MainLayout keeps warm.  `enablePolling: false`
  // means this call doesn't spin up a second interval or start a fetch — it
  // only reads the cache populated by MainLayout's useStatusSummary(true).
  // When the `unified_badge_poll_disabled` flag is set, isDisabled=true and we
  // fall back to the legacy individual poll below.
  const summary = useStatusSummary(false, false);

  // Legacy individual poll — active ONLY when the unified endpoint is disabled
  // for this tenant (emergency fallback via the feature flag).
  const countInterval = useJitteredInterval(60_000);
  const { data: legacyCountData } = useGetAlertsUnreadCount({
    query: {
      refetchInterval: summary.isDisabled ? countInterval : false,
      staleTime: countInterval,
      refetchOnWindowFocus: summary.isDisabled,
      refetchIntervalInBackground: false,
      queryKey: getGetAlertsUnreadCountQueryKey(),
      enabled: summary.isDisabled,
    },
  });

  // Pick count source: unified summary (default) or legacy poll (fallback).
  const unread = summary.isDisabled
    ? (legacyCountData?.count ?? 0)
    : summary.unreadAlerts;

  // List query — lazy, only fires when popover opens.
  const { data: listData, isLoading } = useListAlerts(
    { limit: 50 },
    { query: { enabled: open, queryKey: getListAlertsQueryKey({ limit: 50 }) } },
  );
  const items = listData?.items ?? [];

  // Colour the badge by the highest-priority *unread* item currently loaded.
  // The list only loads when the popover opens, so before that we fall back
  // to the default red so the badge is always visible when there are unreads.
  const topPriority = highestUnreadPriority(items);

  const markOne = useMarkAlertRead({
    mutation: {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: getGetAlertsUnreadCountQueryKey() });
        void qc.invalidateQueries({ queryKey: STATUS_SUMMARY_QUERY_KEY });
        void qc.invalidateQueries({ queryKey: getListAlertsQueryKey({ limit: 50 }) });
      },
    },
  });
  const markAll = useMarkAllAlertsRead({
    mutation: {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: getGetAlertsUnreadCountQueryKey() });
        void qc.invalidateQueries({ queryKey: STATUS_SUMMARY_QUERY_KEY });
        void qc.invalidateQueries({ queryKey: getListAlertsQueryKey({ limit: 50 }) });
      },
    },
  });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={unread > 0 ? `Notifications (${unread} unread)` : "Notifications"}
          title="Notifications"
          data-testid="nav-notifications"
          className="relative"
        >
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span
              className={`absolute top-1 right-1 min-w-[1.1rem] h-[1.1rem] px-1 rounded-full text-white text-[10px] font-semibold flex items-center justify-center ${badgeColorClass(topPriority)}`}
              data-testid="notifications-badge"
              data-priority={topPriority ?? undefined}
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0">
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <div className="text-sm font-medium">Notifications</div>
          <Button
            variant="ghost"
            size="sm"
            disabled={unread === 0 || markAll.isPending}
            onClick={() => markAll.mutate()}
            data-testid="notifications-mark-all-read"
            className="h-7 text-xs"
          >
            <CheckCheck className="h-3.5 w-3.5 mr-1" />
            Mark all read
          </Button>
        </div>
        <ScrollArea className="max-h-[28rem]">
          {isLoading ? (
            <div className="p-6 text-center text-sm text-muted-foreground">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              You&rsquo;re all caught up.
            </div>
          ) : (
            <ul className="divide-y">
              {items.map((a) => {
                const row = (
                  <div className="flex gap-2 p-3 hover:bg-accent/40 transition-colors">
                    <div
                      className={`mt-1 h-2 w-2 rounded-full flex-shrink-0 ${
                        a.read ? "bg-transparent" : dotColorClass(a.priority)
                      }`}
                      data-priority={a.priority}
                      aria-hidden
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className={`text-sm ${a.read ? "text-muted-foreground" : "font-medium"}`}>
                          {a.title}
                        </div>
                        <span className="text-[10px] text-muted-foreground whitespace-nowrap mt-0.5">
                          {timeAgo(a.createdAt)}
                        </span>
                      </div>
                      {a.body && (
                        <div className="text-xs text-muted-foreground mt-0.5 truncate">{a.body}</div>
                      )}
                    </div>
                    {!a.read && (
                      <button
                        type="button"
                        aria-label="Mark as read"
                        title="Mark as read"
                        className="opacity-60 hover:opacity-100"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          markOne.mutate({ id: a.id });
                        }}
                      >
                        <Check className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                );
                return (
                  <li key={a.id} data-testid={`notification-${a.id}`}>
                    {a.link ? (
                      <Link
                        href={a.link}
                        onClick={() => {
                          if (!a.read) markOne.mutate({ id: a.id });
                          setOpen(false);
                        }}
                      >
                        {row}
                      </Link>
                    ) : (
                      row
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </ScrollArea>
        <div className="border-t px-3 py-2">
          <Link
            href="/notifications"
            onClick={() => setOpen(false)}
            className="block text-center text-xs font-medium text-primary hover:underline"
            data-testid="notifications-view-all"
          >
            View all notifications
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
